import {useMemo} from 'react';
import {MapView} from '@molecules';
import {HereAndNowWorkspacesMapProps} from './types';
import {useAppSelector, useUserProfilePics} from '@hooks';
import {
  ConnectionUser,
  getConnectedUserEntities,
  getIsMapsUsabilityVariantsEnabled,
  getIsThreeDMapsEnabled,
  getNodeInformationPerAreaByBuildingId,
  getWorkspaceOccupancyForBuildingAndFloorId,
  getWorkspaceReservationsForConnectionsByDate,
} from '@lib/store';
import {toMapViewUser} from '@utils';
import {useDesksGroupedByStatus} from '@lib/logic';
import {createSetStyleConfig, createSetStyleConfigOverrideEntry, createState, mergeState} from '@lib/utils';
import {AreaEventTarget} from '../../../../../../../submodules/map/mapiq-map/EventTarget';
import {AreaMarkerData, MarkerData} from '../../../../../../../submodules/map/mapiq-map/markers/NodeMarkerData';
import {format, startOfDay} from 'date-fns';
import {useFacilityMarkers} from '../useFacilityMarkers';
import {useTheme} from 'styled-components';
import {createSensorHighlightsForAreas} from '@lib/logic';
import {EventTarget} from '@map/mapiq-map/EventTarget';
import {VariantSelector} from '../HaNRoomsMap/VariantSelector';
import {useUsabilityVariant} from '../HaNMaps/useUsabilityVariant';
import {useConnectionReservationsWithUsers} from '@lib/store/src/ui/hooks/timeslots/useConnectionReservationsWithUsers';
import {dateToLocalDateString, dateToLocalDateTimeString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {useHereAndNow} from '@pages';

export const HaNWorkspacesMap = ({
  // TODO: why is floorId nullable?
  floorId,
  viewportRestrictions,
  buildingId,
  onAreaClick,
  onOutsideClick,
  selectedArea,
  hoverArea,
}: HereAndNowWorkspacesMapProps) => {
  const {activeVariant} = useUsabilityVariant();
  const theme = useTheme();

  const {filterData, setHoverRoomId} = useHereAndNow();
  const workspaces = filterData.workspaces.filteredData;

  const isMapsUsabilityVariantsEnabled = useAppSelector(getIsMapsUsabilityVariantsEnabled);
  const isThreeDMapsEnabled = useAppSelector(getIsThreeDMapsEnabled);
  const areaBookingStates = useAppSelector((state) =>
    getNodeInformationPerAreaByBuildingId(state, startOfDay(new Date()).toISOString(), buildingId),
  );

  const params = useMemo(
    () => ({
      date: new Date().toISOString(),
      buildingId,
    }),
    [buildingId],
  );

  // Connections
  const now = new Date();
  const todayKey = dateToLocalDateString(now);
  const nowKey = dateToLocalDateTimeString(now); // TODO: this should ideally increment every now and then
  const connections_v2 = useConnectionReservationsWithUsers(todayKey) // TODO: this should be refetched every now and then
    // Ongoing
    .filter((data) => nowKey >= data.reservation.localStart && nowKey <= data.reservation.localEnd)
    // On a desk (not an area) on the displayed floor
    .filter((data) => 'deskId' in data.reservation.workspace && data.reservation.workspace.floorId === floorId);

  // This transforms the data from our new slice to whatever we had before...
  const connectionReservations = connections_v2.flatMap((data) =>
    'deskId' in data.reservation.workspace
      ? {
          floorId: data.reservation.workspace.floorId,
          deskId: data.reservation.workspace.deskId,
          userId: data.user.id,
        }
      : [],
  );

  const connections = useAppSelector(getConnectedUserEntities);
  const connectionProfileImages = useUserProfilePics(
    Object.values(connections)
      .filter((c): c is ConnectionUser => c !== undefined)
      .map((c) => ({
        userId: c.id,
        imageHash: c.imageHash ?? '',
      })),
  );

  // Sensor data
  //// Sensor highlights might not be available if (a) FF is turned off, or (b) there are just not any sensors
  const sensorData = useAppSelector((state) => getWorkspaceOccupancyForBuildingAndFloorId(state, buildingId, floorId));
  //// When the sensor data is loaded but there's nothing with a state, we fall back to just regular area markers.
  const doNotShowSensorData =
    sensorData?.status === 'failed' ||
    (sensorData?.status === 'loaded' && sensorData?.states.length === 0) ||
    !sensorData;

  // Desks grouped by status
  const groupedDesks = useDesksGroupedByStatus({
    floorId,
    sensorData,
    connectionReservations,
    doNotShowSensorData,
  });

  // TODO: shouldn't this happen one layer up?
  const workspacesOnFloor = useMemo(() => workspaces.filter((a) => a.floorId === floorId), [floorId, workspaces]);

  const sensorHighlights = useMemo(() => {
    if (sensorData?.status !== 'loaded') return [];

    const deskReservations = connectionReservations
      .filter((f) => f.floorId === floorId && f.deskId)
      .flatMap((f) => {
        const con = connections[f.userId];

        if (!con) return [];

        return {
          deskId: f.deskId,
          connection: toMapViewUser(
            {
              email: con.email,
              initials: con.initials,
              imageUrl: connectionProfileImages[con.id],
            },
            theme,
          ),
        };
      });

    return createSensorHighlightsForAreas(
      workspacesOnFloor,
      sensorData.states,
      deskReservations,
      isThreeDMapsEnabled,
      isMapsUsabilityVariantsEnabled && activeVariant === 'B',
      areaBookingStates,
    );
  }, [
    sensorData,
    areaBookingStates,
    workspacesOnFloor,
    connectionReservations,
    theme,
    connections,
    floorId,
    connectionProfileImages,
    isThreeDMapsEnabled,
    isMapsUsabilityVariantsEnabled,
    activeVariant,
  ]);

  // Area highlights are used if no sensors are found on the floor
  const capacityMarkersOnFloor: AreaMarkerData[] | MarkerData[] = useMemo(() => {
    if (isThreeDMapsEnabled) {
      const isEntireFloorUntraceable = workspacesOnFloor.every(
        (area) => area.sensorInformation === null && area.workspaceBookingPolicyType === 'NonBookable',
      );

      // If the entire floor is untraceable, we return an untraceable cluster marker for each area
      if (isEntireFloorUntraceable) {
        return workspacesOnFloor.map(
          (area) =>
            ({
              type: 'untraceable',
              subType: 'workspace',
              nodeId: area.id,
              capacity: area.capacity || area.adHocCapacity || 1,
            } as MarkerData),
        );
      }

      // Otherwise, return sensor highlights
      return workspacesOnFloor.map((area) => {
        // Areas that neither have sensor data nor are bookable are considered untraceable
        if (area.sensorInformation === null && area.workspaceBookingPolicyType === 'NonBookable') {
          return {
            type: 'untraceable',
            subType: 'workspace',
            nodeId: area.id,
            capacity: 1,
          };
        }

        const capacity = area.capacity || area.adHocCapacity;
        const areaInfo = (areaBookingStates ?? {})[area.id];
        const nrPresent = areaInfo?.occupation ?? 0;
        const availableCount = capacity - nrPresent;

        return {
          capacity,
          nodeId: area.id,
          type: 'sensor_availability',
          nrPresent,
          nrTentative: 0,
          nrUnknown: 0,
          nrAvailable: availableCount,
        } as MarkerData;
      });
    }

    return workspacesOnFloor.map((area) => {
      const areaInfo = (areaBookingStates ?? {})[area.id];

      return {
        type: 'area',
        nodeId: area.id,
        available: areaInfo ? areaInfo.capacity - areaInfo.occupation : area.capacity || area.adHocCapacity,
        connections: [],
      };
    });
  }, [areaBookingStates, workspacesOnFloor, isThreeDMapsEnabled]);

  const facilityMarkers = useFacilityMarkers(buildingId, floorId);

  const highlights = useMemo(() => {
    const base: MarkerData[] = doNotShowSensorData ? capacityMarkersOnFloor : sensorHighlights;
    return base.concat(facilityMarkers);
  }, [doNotShowSensorData, capacityMarkersOnFloor, sensorHighlights, facilityMarkers]);

  // States
  //// Areas
  const areaStates = useMemo(() => {
    const highlightState = createState(
      workspacesOnFloor.map((a) => a.id),
      'highlighted',
    );

    const selectionState = createState(selectedArea ? [selectedArea.id] : [], 'selected');

    return mergeState(highlightState, selectionState);
  }, [workspacesOnFloor, selectedArea]);

  //// Desks
  const deskStates = useMemo(() => {
    return createState(
      [
        ...groupedDesks.available.map((desk) => desk.id),
        ...groupedDesks.occupied.map((desk) => desk.id),
        ...groupedDesks.unknown.map((desk) => desk.id),
      ],
      'highlighted',
    );
  }, [groupedDesks]);

  //// All nodes
  const nodeStates = useMemo(() => {
    if (!isThreeDMapsEnabled) return areaStates;

    return mergeState(areaStates, deskStates);
  }, [areaStates, deskStates, isThreeDMapsEnabled]);

  const elementsToKeepInView = useMemo(() => (selectedArea ? [selectedArea.id] : undefined), [selectedArea]);
  const spotlightId = useMemo(() => (hoverArea?.floorId === floorId ? hoverArea.id : null), [hoverArea, floorId]);
  const nodeStyles = useMemo(() => {
    if (!isThreeDMapsEnabled) return undefined;

    const available = activeVariant === 'B' ? 'availableB' : 'available';
    const occupied = activeVariant === 'B' ? 'occupiedB' : 'occupied';

    return createSetStyleConfig({
      preset: 'secondary',
      override: new Map([
        ...groupedDesks.available.map((desk) => createSetStyleConfigOverrideEntry(desk.id, available)),
        ...groupedDesks.occupied.map((desk) => createSetStyleConfigOverrideEntry(desk.id, occupied)),
        ...groupedDesks.unknown.map((desk) => createSetStyleConfigOverrideEntry(desk.id, 'seemsToBeAvailable')),
      ]),
    });
  }, [activeVariant, groupedDesks, isThreeDMapsEnabled]);

  const handleClick = (e: AreaEventTarget) => {
    // If the area is already selected, don't trigger event
    if (e.areaId === selectedArea?.id) return;

    // Area was clicked, trigger event
    if (e.areaId) {
      onAreaClick(e);
      return;
    }

    // Effectively remove selected state
    onOutsideClick();
  };

  const handleHover = (e: EventTarget) => {
    if (!isThreeDMapsEnabled) return;

    const validId = e.areaId ?? e.roomId ?? e.facilityId;
    if (validId) {
      setHoverRoomId?.(validId);
      return;
    }

    setHoverRoomId?.(null);
  };

  return (
    <>
      <MapView
        {...(isMapsUsabilityVariantsEnabled && {key: activeVariant})}
        borderRadius={0}
        floorId={floorId}
        buildingId={buildingId}
        highlights={highlights}
        fullView={true}
        elementsToKeepInView={elementsToKeepInView}
        disablePointerEvents={false}
        buildingNodeStates={nodeStates}
        buildingNodeTypeStates={new Map([['facility', 'dimmed']])}
        viewportRestrictions={viewportRestrictions}
        onClick={(e) => handleClick(e as AreaEventTarget)}
        onHover={handleHover}
        spotlightId={spotlightId}
        type="preference"
        nodeStyles={nodeStyles}
      />

      {isThreeDMapsEnabled && isMapsUsabilityVariantsEnabled && <VariantSelector />}
    </>
  );
};
